/**
## Field Input

`SqFieldInputComponent` is a variation on a classic Ember TextField element. While
technically the input is standalone, it's really intended to be nested inside a
Form Field block component. The two components communicate with each other over
event handling (e.g. focus, error). Note if a `placeholder` is not provided, Field
Input will look to the wrapping field label when applicable.

Parameters to the component can follow anything you'd pass to Ember TextField/Input.

```example
@title Basic Example in Form Field

@controller-properties
{
  exampleFirstName: 'Alex'
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label=(t "common.firstName")}}
      {{sq-field-input value=exampleFirstName}}
    {{/sq-form-field}}
  </div>
</div>
```

```example
@title Two Inputs Per Row w/ Disabled, Read Only
@description Notice how we add isDisabled or isReadOnly on the form field, and disabled again on the inner text.

@controller-properties
{
  exampleFirstName: 'Alex',
  exampleLastName: 'Johnson',
  exampleEmail: 'blah@blahblah.com',
  examplePhone: null
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label=(t "common.firstName")}}
      {{sq-field-input value=exampleFirstName
                       placeholder=(t "common.help")}}
    {{/sq-form-field}}
    {{#sq-form-field label=(t "common.lastName") isDisabled=true}}
      {{sq-field-input value=exampleLastName
                       disabled=true}}
    {{/sq-form-field}}
  </div>
  <div class="form-row">
    {{#sq-form-field label=(t "common.email")}}
      {{sq-field-input value=exampleEmail
                       placeholder=""}}
    {{/sq-form-field}}
    {{#sq-form-field label=(t "common.phone") isReadOnly=true}}
      {{sq-field-input value=examplePhone
                       disabled=true}}
    {{/sq-form-field}}
  </div>
</div>
```

### Parameters (Common)
@param {String} value - The value attribute of the input element. As the user inputs text, this property is automatically updated.
@param {String} placeholder - Placeholder text. When not defined, it tries to pull the associated wrapper label.
@param {Boolean} [disabled=false] - If the input is disabled from further changes.

Sass dependencies at: `app/assets/stylesheets/bridgeport/components/form-elements/formfield/_formfield.sass`
*/

import { scheduleOnce } from '@ember/runloop';
import { TextField } from '@ember/legacy-built-in-components';
import { isPresent, isNone } from '@ember/utils';
import FieldElementSupport from '../utils/field-element-support';

// eslint-disable-next-line ember/no-classic-classes
export default TextField.extend(FieldElementSupport, {
  autofocus: false,
  classNames: 'form-field__input',
  placeholder: null,
  didInsertElement(...args) {
    this._super(...args);
    if (this.get('autofocus')) {
      scheduleOnce('afterRender', this, this.focusElement);
    }

    // if a placeholder isn't explicitly set, match wrapping field label
    if (isNone(this.get('placeholder'))) {
      const label = this.get('parentView.label');
      if (isPresent(label)) {
        this.set('placeholder', label);
      }
    }
  },
  focusElement() {
    this.get('element').focus();
  }
});